import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { getCleanOptions } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ACTIVE_MONTH_GREEN = '#22c55e';

/**
 * FOB Seasonality Chart
 * Shows when each origin is "in season" based on volume data
 * Uses cascading filters: Year → Origins
 */
const FOBSeasonalityChart = ({
  data = [],
  origins: originsProp = [],
  years: yearsProp = [],
  commodity = '',
}) => {
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedOrigins, setSelectedOrigins] = useState([]);

  // Modal states
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);

  // Track if initial load has run
  const initializedRef = useRef(false);

  const rowYear = useCallback((item) => item.year ?? item.iso_year, []);

  const rowMonth = useCallback((item) => item.month ?? item.calendar_month, []);

  const rowVolume = useCallback((item) => {
    const v = item.volume ?? item.total_volume;
    return typeof v === 'number' ? v : parseFloat(v);
  }, []);

  // Derive years from data if prop is empty (more robust)
  const derivedYears = useMemo(() => {
    if (yearsProp && yearsProp.length > 0) return yearsProp;
    if (!data || data.length === 0) return [];
    // Extract unique years from data (API may use year or iso_year)
    const yearsFromData = [...new Set(data.map((item) => rowYear(item)).filter(Boolean))];
    return yearsFromData;
  }, [yearsProp, data, rowYear]);

  // Sort years descending (most recent first)
  const sortedYears = useMemo(() => [...derivedYears].sort((a, b) => b - a), [derivedYears]);

  // Get available origins for selected year (fallback to origins prop)
  const availableOrigins = useMemo(() => {
    if (!data || data.length === 0) {
      // Fallback to origins prop if data is empty
      return originsProp || [];
    }

    let filtered = data;
    if (selectedYear) {
      filtered = filtered.filter((item) => rowYear(item) === selectedYear);
    }

    // Try to get origins from data
    const originsFromData = getCleanOptions(filtered, 'origin', commodity);

    // If no origins found in data, fall back to prop
    if (originsFromData.length === 0 && originsProp && originsProp.length > 0) {
      return originsProp;
    }

    return originsFromData;
  }, [data, selectedYear, originsProp, rowYear, commodity]);

  // Count active months per origin for a given year's data
  const getActiveMonthsCountByOrigin = useCallback((yearFilteredData) => {
    const byOrigin = {};
    yearFilteredData.forEach(item => {
      const origin = item.origin;
      const month = rowMonth(item);
      const volume = rowVolume(item);
      if (!origin || !month) return;
      if (volume && volume > 0) {
        if (!byOrigin[origin]) byOrigin[origin] = {};
        byOrigin[origin][month] = true;
      }
    });
    const counts = {};
    Object.keys(byOrigin).forEach(origin => {
      counts[origin] = Object.keys(byOrigin[origin]).length;
    });
    return counts;
  }, [rowMonth, rowVolume]);

  // Top 10 origins by active months count, then sorted by name
  const getTop10OriginsByActiveMonths = useCallback((yearFilteredData) => {
    let originsForYear = getCleanOptions(yearFilteredData, 'origin', commodity);
    if (originsForYear.length === 0) return [];
    const counts = getActiveMonthsCountByOrigin(yearFilteredData);
    const sortedByCount = [...originsForYear].sort(
      (a, b) => (counts[b] || 0) - (counts[a] || 0)
    );
    const top10 = sortedByCount.slice(0, 10);
    return [...top10].sort((a, b) => a.localeCompare(b));
  }, [getActiveMonthsCountByOrigin, commodity]);

  const hydrateSeasonality = useCallback(
    (s) => {
      if (!data?.length || !sortedYears.length) return;
      initializedRef.current = true;
      const year =
        s.selectedYear != null && sortedYears.includes(s.selectedYear) ? s.selectedYear : sortedYears[0];
      setSelectedYear(year);
      const yearFiltered = data.filter((item) => rowYear(item) === year);
      let originsForYear = getCleanOptions(yearFiltered, 'origin', commodity);
      if (originsForYear.length === 0) originsForYear = getCleanOptions(data, 'origin', commodity);
      if (originsForYear.length === 0 && originsProp?.length) originsForYear = [...originsProp];
      if (Array.isArray(s.selectedOrigins) && s.selectedOrigins.length) {
        const valid = s.selectedOrigins.filter((o) => originsForYear.includes(o));
        setSelectedOrigins(valid.length ? valid : originsForYear);
      } else {
        // Multi-select default: select ALL origins for the year.
        setSelectedOrigins(originsForYear);
      }
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
    },
    [data, sortedYears, originsProp, rowYear, commodity]
  );

  // Reset before Firestore hydrate runs for a new commodity scope (useLayoutEffect runs before useEffect).
  useLayoutEffect(() => {
    initializedRef.current = false;
  }, [commodity]);

  const snapshotSeasonality = useCallback(
    () => ({
      selectedYear,
      selectedOrigins,
      filtersExpanded,
    }),
    [selectedYear, selectedOrigins, filtersExpanded]
  );

  usePersistWidgetSnapshot(
    'fob-seasonality',
    commodity,
    !!(data?.length && sortedYears.length),
    hydrateSeasonality,
    snapshotSeasonality,
    [selectedYear, selectedOrigins, filtersExpanded]
  );

  // Initialize on first data load
  useEffect(() => {
    if (!data || data.length === 0 || initializedRef.current) return;
    if (sortedYears.length === 0) return; // Wait for years to be available

    initializedRef.current = true;

    // Set year default - current year (most recent)
    const defaultYear = sortedYears[0];
    setSelectedYear(defaultYear);

    const yearFiltered = data.filter((item) => rowYear(item) === defaultYear);
    let originsForYear = getCleanOptions(yearFiltered, 'origin', commodity);

    if (originsForYear.length === 0) {
      originsForYear = getCleanOptions(data, 'origin', commodity);
    }
    if (originsForYear.length === 0 && originsProp && originsProp.length > 0) {
      originsForYear = originsProp;
    }

    // Multi-select default: select ALL origins for the year.
    setSelectedOrigins(originsForYear);
  }, [data, sortedYears, originsProp, rowYear, commodity]);

  // Handler for YEAR change - keep origins still valid for new year, else default to top 10
  const handleYearChange = useCallback((newYear) => {
    const yearNum = Number(newYear);
    setSelectedYear(yearNum);

    const yearFiltered = data.filter((item) => rowYear(item) === yearNum);
    let originsForYear = getCleanOptions(yearFiltered, 'origin', commodity);

    if (originsForYear.length === 0) {
      originsForYear = getCleanOptions(data, 'origin', commodity);
    }
    if (originsForYear.length === 0 && originsProp && originsProp.length > 0) {
      originsForYear = originsProp;
    }

    const kept = selectedOrigins.filter(o => originsForYear.includes(o));
    const top10 = getTop10OriginsByActiveMonths(yearFiltered);
    setSelectedOrigins(kept.length > 0 ? kept : (top10.length > 0 ? top10 : originsForYear));
  }, [data, selectedOrigins, originsProp, getTop10OriginsByActiveMonths, rowYear, commodity]);

  // Calculate seasonality map from volume data
  // An origin is "in season" for a month if there's volume > 0
  const seasonalityMap = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Filter by selected year
    let filtered = data;
    if (selectedYear) {
      filtered = filtered.filter((item) => rowYear(item) === selectedYear);
    }

    const byOrigin = {};
    filtered.forEach(item => {
      const origin = item.origin;
      const month = rowMonth(item);
      const volume = rowVolume(item);

      if (!origin || !month) return;
      // Only mark as in season if volume > 0
      if (volume && volume > 0) {
        if (!byOrigin[origin]) byOrigin[origin] = {};
        byOrigin[origin][month] = true;
      }
    });

    return byOrigin;
  }, [data, selectedYear, rowYear, rowMonth, rowVolume]);

  const handleOriginToggle = (origin) => {
    if (selectedOrigins.includes(origin)) {
      setSelectedOrigins(selectedOrigins.filter(o => o !== origin));
    } else {
      setSelectedOrigins([...selectedOrigins, origin]);
    }
  };

  const getOriginDisplayText = () => {
    if (selectedOrigins.length === 0) return 'Select regions';
    if (selectedOrigins.length === availableOrigins.length) return 'All regions';
    return `${selectedOrigins.length} selected`;
  };

  const helpDescription = "Shows when each origin is actively supplying the market based on volume data. Colored bars indicate months with supply activity - useful for planning sourcing strategies.";

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <div className="border-y border-[#5D625F] py-2 px-4 flex-shrink-0">
          <div className="flex items-center gap-1">
            <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
              Active USA Exporter and Producer Seasonality Overview
            </h3>
            <ChartHelpButton title="Seasonality Overview" description={helpDescription} />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400 text-center" style={{ fontSize }}>
            No seasonality data available.<br />Select a different commodity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
      <div className="border-b border-[#5D625F] py-2 flex-shrink-0">
        <div className="flex items-center gap-1">
          <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
            Active USA Exporter and Producer Seasonality Overview - {selectedYear || '...'}
          </h3>
          <ChartHelpButton title="Seasonality Overview" description={helpDescription} />
        </div>
      </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 px-4">
        {/* Filters Header */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="w-full flex items-center justify-between py-2 border-b border-[#3a3c3b] flex-shrink-0"
        >
          <span className="text-gray-400 font-medium" style={{ fontSize: fontSize - 2 }}>Filters</span>
          {filtersExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>

        {filtersExpanded && (
          <div className="py-2 space-y-2 flex-shrink-0">
            {/* Year and Region Filters */}
            <div className="grid grid-cols-2 gap-2">
              <FilterDropdown
                label="Year"
                value={selectedYear || '-'}
                onClick={() => setYearModalOpen(true)}
              />
              <FilterDropdown
                label="Regions"
                value={getOriginDisplayText()}
                onClick={() => setOriginModalOpen(true)}
              />
            </div>
          </div>
        )}

        {/* Seasonality Table */}
        <div className="flex-1 min-h-0 overflow-auto mt-2">
          {selectedOrigins.length > 0 ? (
            <table className="w-full min-w-max">
              <thead>
                <tr>
                  <th className="text-left text-gray-400 p-2 text-xs font-medium sticky left-0 border-b" style={{ backgroundColor: theme.stickyHeaderBg, borderColor: theme.borderSecondary }}>
                    Origin
                  </th>
                  {MONTH_LABELS.map(m => (
                    <th key={m} className="text-center text-gray-500 p-2 text-xs font-normal border-b border-[#3a3c3b]" style={{ fontSize: fontSize - 2 }}>
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedOrigins.map((origin, idx) => (
                  <tr key={origin} className="border-b border-[#2a2c2b]">
                    <td className="p-2 text-sm sticky left-0 text-black dark:text-gray-300" style={{ fontSize: fontSize - 1, backgroundColor: theme.stickyHeaderBg }}>
                      {origin}
                    </td>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(month => (
                      <td key={month} className="p-2 text-center">
                        <div
                          className="w-6 h-4 mx-auto rounded-sm"
                          style={{
                            backgroundColor: seasonalityMap[origin]?.[month]
                              ? ACTIVE_MONTH_GREEN
                              : '#374151'
                          }}
                          title={`${MONTH_LABELS[month-1]}: ${seasonalityMap[origin]?.[month] ? 'In Season' : 'Off Season'}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400 text-sm">Select at least one region</p>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center py-3 text-xs text-gray-400 flex-shrink-0 border-t border-[#3a3c3b]">
          <span className="flex items-center gap-2">
            <span className="w-4 h-3 bg-gray-600 rounded-sm" />
            Off Season
          </span>
        </div>
      </div>

      {/* Origin Modal - Multi-select */}
      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title="Select Regions"
        options={availableOrigins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginToggle}
        multiSelect
        onSelectAll={() => setSelectedOrigins([...availableOrigins])}
        onClearAll={() => setSelectedOrigins([])}
      />

      {/* Year Modal */}
      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title="Select Year"
        options={sortedYears.map(String)}
        selectedValues={selectedYear ? String(selectedYear) : ''}
        onSelect={handleYearChange}
      />
    </div>
  );
};

export default FOBSeasonalityChart;
